/**
 * AnnouncementsScreen — lista de comunicados de la comunidad.
 * A = abrir detalle, B = back.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useApp } from '@/state/store';
import { sfx } from '@/lib/audio';
import { announcementsApi, notificationsApi, type Announcement, ApiError } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { MegaphoneSprite, LoadingSprite } from '@/components/Sprites';

export function AnnouncementsScreen() {
  const { goto, openDetail, openModal, user } = useApp();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(0);
  const handlersRef = useRef({ items, selected, openDetail, openModal });
  handlersRef.current = { items, selected, openDetail, openModal };

  const load = useCallback(async () => {
    try {
      const res = await announcementsApi.list();
      setItems(res.announcements || []);
    } catch (err) {
      if (err instanceof ApiError) console.error('Announcements load:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-mark all unread notifications as read on mount so the
  // NOTICIAS badge clears once the user opens this screen.
  useEffect(() => {
    notificationsApi.markAllRead().catch(() => { /* */ });
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = (delta: number) => {
    if (items.length === 0) return;
    const next = Math.max(0, Math.min(items.length - 1, selected + delta));
    if (next !== selected) {
      setSelected(next);
      sfx.tick();
    }
  };

  useEffect(() => {
    const onUp = () => move(-1);
    const onDown = () => move(1);
    const onA = () => {
      const h = handlersRef.current;
      const item = h.items[h.selected];
      if (item) { sfx.confirm(); h.openDetail({ id: item.id, type: 'announcement' }); }
    };
    const onB = () => { sfx.cancel(); goto('home'); };
    window.addEventListener('vicin-gb:up', onUp);
    window.addEventListener('vicin-gb:down', onDown);
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:up', onUp);
      window.removeEventListener('vicin-gb:down', onDown);
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, [goto]);

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <MegaphoneSprite size={11} />
          <span className="gb-pix gb-pix-xs">NOTICIAS · {items.length}</span>
        </div>
        <div className="gb-status-right">
          {isAdmin && (
            <button
              onClick={() => { sfx.tick(); openModal('create-announcement'); }}
              className="gb-pix gb-pix-xs"
              style={{
                background: 'var(--gb-mid)',
                color: 'var(--gb-bg)',
                border: 'none',
                padding: '1px 6px',
                fontSize: 5,
                cursor: 'pointer',
                borderRadius: 2,
              }}
              data-testid="announcements-create"
            >
              + NUEVA
            </button>
          )}
        </div>
      </div>

      <div className="gb-body" style={{ padding: '0', gap: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="gb-center" style={{ gap: 8 }}>
            <LoadingSprite size={32} />
            <div className="gb-pix gb-pix-xs gb-mid-text">CARGANDO...</div>
          </div>
        ) : items.length === 0 ? (
          <div className="gb-center" style={{ gap: 8 }}>
            <MegaphoneSprite size={48} />
            <div className="gb-pix gb-pix-xs gb-mid-text">SIN NOTICIAS</div>
            <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, maxWidth: 200, textAlign: 'center' }}>
              Cuando tu comunidad publique algo, aparecerá acá.
            </div>
          </div>
        ) : (
          <div className="gb-list" style={{ overflowY: 'auto', flex: 1 }}>
            {items.map((a, i) => {
              const sel = i === selected;
              return (
                <button
                  key={a.id}
                  className="gb-list-item"
                  data-selected={sel}
                  onClick={() => { setSelected(i); sfx.confirm(); openDetail({ id: a.id, type: 'announcement' }); }}
                  onMouseEnter={() => setSelected(i)}
                  data-testid={`announcement-${a.id}`}
                >
                  <div className="gb-list-row">
                    {a.priority === 'urgent' && <span className="gb-list-badge gb-list-badge--danger">!</span>}
                    {a.priority === 'important' && <span className="gb-list-badge gb-list-badge--pending">i</span>}
                    <span className="gb-list-title">{a.title}</span>
                  </div>
                  <div className="gb-list-row gb-list-meta">
                    <span>{formatRelative(a.created_at)}</span>
                    {a.author_name && <span>· {a.author_name}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}